import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  buildForm,
  buildHtml,
  buildLink,
  buildUrl,
  ContentRoute,
  RouteSlotInput,
} from '@caritas/ui';
import { navigate } from 'gatsby';

import {
  Donation,
  SponsorshipDonation,
  StockDonation,
} from '../../../generated/operation-ids';
import { useMenus } from '../../commons/user-menus';
import { mapBreadcrumbItems } from '../../utils/build-breadcrumbs';
import { isProject } from '../../utils/is-project';
import { languageFallback } from '../../utils/localizations';
import { mapImage } from '../Image.mappers';
import { buildCopyrightText } from './Image.mappers';

export function hasImage(
  input: any | null,
): input is { image: ImageFragment; caption?: string } {
  return !!input?.image;
}

function getAvailableAmounts(
  donation?: (Donation & StockDonation & SponsorshipDonation) | {},
) {
  if (!donation) {
    // @TODO think we should have a default configuration here - wherever it's most needed donation purpose
    return [];
  }
  // @ts-ignore
  switch (donation?.__typename) {
    case 'Donation':
      // @ts-ignore
      return donation.oneTimeAmounts?.amounts ?? ['60', '120', 240, '*'];
      break;
    case 'StockDonation':
      // @ts-ignore
      return donation.oneTimeAmounts?.amounts;
      break;
    case 'SponsorshipDonation':
      // @ts-ignore
      return donation.monthlyAmounts?.amounts;
      break;
    default:
      return [];
  }
}

function getDefaultAmount(
  donation?: (Donation & StockDonation & SponsorshipDonation) | {},
) {
  let amount: string = '60';
  let period: string = 'oneTime';
  // @ts-ignore
  switch (donation?.__typename) {
    case 'Donation':
      // @ts-ignore
      if (donation?.oneTimeAmounts?.default) {
        // @ts-ignore
        amount = `${donation.oneTimeAmounts.default}`;
        period = `oneTime`;
      }
      break;
    // @ts-ignore
    case 'StockDonation':
      // @ts-ignore
      if (donation?.oneTimeAmounts?.default) {
        // @ts-ignore
        amount = `${donation.oneTimeAmounts.default}`;
        period = `oneTime`;
      }
      break;
    // @ts-ignore
    case 'SponsorshipDonation':
      // @ts-ignore
      if (donation?.monthlyAmounts?.default) {
        // @ts-ignore    // @ts-ignore
        amount = `${donation.monthlyAmounts.default}`;
        period = `monthly`;
      }
      break;
    default:
      return { amount };
  }
  return { amount, period };
}

export function mapHeaderImageCopyright(input: {
  media?: {
    caption?: string;
    source?: string;
  };
}): RouteSlotInput<typeof ContentRoute, 'body', 'headerImageCopyright'> {
  return {
    key: 'headerImageCopyright',
    input: () => [
      {
        text: buildCopyrightText(input.media),
      },
      200,
    ],
  };
}

export const mapContentPageHeader = (
  input: ContentPageHeaderFragment,
  pageContext: SilverbackPageContext,
  path: string,
  date?: { start: string; end?: string },
  donationPurpose?: (Donation & StockDonation & SponsorshipDonation) | {},
  donationDescription?: string,
  format?: {termId: string}[],
): RouteSlotInput<typeof ContentRoute, 'body', 'heroSection'> => {
  return {
    key: 'heroSection',
    input: function useContentPageHeader() {
      const image = hasImage(input.media) ? input.media.image : undefined;
      const {
        menus: { main },
      } = useMenus(pageContext);

      const initialValues = getDefaultAmount(donationPurpose);

      let availableAmounts = getAvailableAmounts(donationPurpose);

      const displayCustomAmount = availableAmounts?.includes('*');

      if (displayCustomAmount) {
        availableAmounts = availableAmounts?.filter(
          (amount: string) => amount !== '*',
        );
      }
      return [
        {
          title: input.headline,
          description: input.lead,
          Image:
            image &&
            mapImage({
              image,
              preset: 'header_landscape',
              // @ts-ignore
              caption: input.media?.caption,
            }),
          MobileImage:
            image &&
            mapImage({
              image,
              preset: 'header_portrait',
              sizes: '',
              // @ts-ignore
              caption: input.media?.caption,
            }),
          CallToAction:
            input?.ctaUrl && input?.ctaText
              ? {
                  Link: buildLink({ href: input.ctaUrl }),
                  title: input.ctaText,
                }
              : undefined,
          HomeLink: buildLink({
            href: `/${languageFallback(pageContext.locale || 'de')}`,
          }),
          breadcrumbItems: mapBreadcrumbItems(path, main.items),
          urgentStyle: input?.displayMode === 'disaster_relief',
          date: date,
          isProject: isProject(format),
          fullScreenImage: input?.displayMode === 'microsite',
          climateChange:
            // @ts-ignore
            donationPurpose?.__typename === 'StockDonation' &&
            availableAmounts?.length &&
            donationPurpose &&
            input.showDonationWidget
              ? {
                  Title: buildHtml(
                    // @ts-ignore
                    `<h2 class="prose-h4">${donationPurpose.callToAction}</h2>`,
                  ),
                  // We get available amounts based on type of donation purpose
                  items: availableAmounts
                    ? availableAmounts.map((amount: number) => ({
                        amount: `${amount}`,
                      }))
                    : [],
                  displayCustomAmount,
                  Form: buildForm({
                    initialValues,
                    onSubmit: (values) => {
                      // Redirect to donation page with state
                      // @ts-ignore
                      navigate(
                        // @ts-ignore
                        buildUrl([donationPurpose.path], {
                          // @ts-ignore
                          amount: values?.customAmount || values.amount,
                          // @ts-ignore
                          custom: !!values?.customAmount,
                          ...(donationDescription
                            ? { description: donationDescription }
                            : {}),
                        }),
                        {
                          // We need to pass values for the RaiseNow widget here
                          // - amount: selected by visitor
                          // - frequency: static based on donation purpose type
                          //       - Simple donation and climate share it's always one-time
                          //       - sponsorship it's monthly
                        },
                      );
                    },
                  }),
                }
              : undefined,
          donation:
            // @ts-ignore
            donationPurpose?.__typename === 'Donation' &&
            availableAmounts?.length &&
            donationPurpose &&
            input.showDonationWidget
              ? {
                  Title: buildHtml(
                    // @ts-ignore
                    `<h2 class="prose-h4">${donationPurpose.callToAction}</h2>`,
                  ),
                  // We get available amounts based on type of donation purpose
                  items: availableAmounts
                    ? availableAmounts.map((amount: number) => ({
                        amount: `${amount}`,
                      }))
                    : [],
                  displayCustomAmount,
                  Form: buildForm({
                    initialValues,
                    onSubmit: (values) => {
                      // Redirect to donation page with state
                      // @ts-ignore
                      navigate(
                        // @ts-ignore
                        buildUrl([donationPurpose.path], {
                          // @ts-ignore
                          amount: values?.customAmount || values.amount,
                          // @ts-ignore
                          custom: !!values?.customAmount,
                          ...(donationDescription
                            ? { description: donationDescription }
                            : {}),
                        }),
                        {
                          // We need to pass values for the RaiseNow widget here
                          // - amount: selected by visitor
                          // - frequency: static based on donation purpose type
                          //       - Simple donation and climate share it's always one-time
                          //       - sponsorship it's monthly
                        },
                      );
                    },
                  }),
                }
              : undefined,
        },
        200,
      ];
    },
  };
};
